import React from "react";
import { graphql, useFragment } from "react-relay";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Tooltip, useModals } from "~/ui";
import Button, { type ButtonProps } from "~/ui/button";

import { useCopilotPaywallsEnabled } from "../meetingRecorder/featureFlag";
import OutOfCreditsModal from "../stream/NoteHeader/OutOfCreditsModal";
import RecordLiveMeetingModal from "./RecordLiveMeetingModal";

import type { RecordWithUrlButton_viewer$key } from "~/__generated__/RecordWithUrlButton_viewer.graphql";

const viewerFragment = graphql`
	fragment RecordWithUrlButton_viewer on Viewer {
		remainingMeetingRecordingCredits
		hasAccessToCopilot
		...LibraryHooksUseOpenChannelsModal_viewer
		...featureFlag_useCopilotPaywallsOn_viewer
	}
`;

export default function RecordWithUrlButton({
	viewer: viewerKey,
	analyticsLocationDescription,
	disabled,
	children,
	...props
}: Omit<ButtonProps, "onClick"> & { viewer: RecordWithUrlButton_viewer$key; analyticsLocationDescription: string }) {
	const modals = useModals();

	const viewer = useFragment(viewerFragment, viewerKey);
	const { remainingMeetingRecordingCredits, hasAccessToCopilot } = viewer;

	const paywallsEnabled = useCopilotPaywallsEnabled(viewer);
	const outOfCredits = remainingMeetingRecordingCredits === 0 && paywallsEnabled;

	const openRecordLiveMeetingModal = React.useCallback(() => {
		dispatchAnalyticsTrack("Clicked start recording with URL", { location: analyticsLocationDescription });
		modals.open(({ close }) => <RecordLiveMeetingModal onClose={close} />);
	}, [analyticsLocationDescription, modals]);

	const openPaywall = React.useCallback(
		() => modals.open(({ close }) => <OutOfCreditsModal onClose={close} />),
		[modals],
	);

	return (
		<Tooltip content="Note Taker is not enabled" placement="bottom" disabled={hasAccessToCopilot}>
			<div>
				<Button
					{...props}
					onClick={outOfCredits ? openPaywall : openRecordLiveMeetingModal}
					disabled={!hasAccessToCopilot || disabled}
				>
					{children}
				</Button>
			</div>
		</Tooltip>
	);
}
